import BaseService from '../../base/base_service';
import Event from '../../utils/event';
import {SYSTEM_EVENTS} from '../../events/events';
import {GEO_LOCATION, GEO_LOCATION_REASONS} from './geo_consts';
import DOM from '../../utils/dom';
import GeoDataFormatter from './geo_data_formatter';
import IGeoService, {
    IGeoBase,
    IGeoLocationParams,
    IGeoRequestParams,
    MAX_RETRIES_FOR_WAITING_PLAYER_MESSAGES_CONNECT
} from "./geo_service_interface";
import _ from "../../utils/underscore";
import Device from "../../utils/device/device";
import {KICK_TYPE} from "../player_messages_service/player_messages_consts";
import {convertStringArrayToString} from "../../../../utils/string-helpers";
import {getIfKnownGeoLockationKickReason, getIfKnownKickReason} from "./geo_location_helper";
import {GeoLocationWrapperFactory} from "./geo_location_wrapper_factory";
import ServiceFactory from "../service_factory";
import {Nullable} from '../../../../common/types/nullable-types';
import Store from "../../utils/store";
import {CHANNEL_SOURCE} from "../../../../common/const/casinofied-analytics-consts";
import {AreaType} from "@sparkware/uc-sdk-core";

const NONE = 'none';
const successGeoFlowCounterKey = 'successGeoFlowCounter';
const maxRetriesForGeoComply = 2;

export default class GeoLocationService extends BaseService implements IGeoService {
    /**
     * @static
     * @type {string}
     */
    static CLASS = 'GeoLocationService';

    private wrapper:IGeoBase = null;
    private geoLocationFreezeWindow: Nullable<{ close: () => void }> = null;
    private geoDataFormatter:GeoDataFormatter = null;
    private geoLocationStartTime:number = 0;
    private geoLocationTimeout:number = null;
    private restrictionChangedEvent:any = null;
    private initParams:IGeoLocationParams;
    private geoInProcess: boolean;
    private playerMessagesConnected: boolean = false;
    private geoComplyRetrynNumber: number = 0;

    public init(params:IGeoLocationParams):void {
        super.init(params);
        this.initParams = params;
        if (this._isEnable() && this._isParamsValid(params)) {
            this._subscribe();
            this.wrapper = new GeoLocationWrapperFactory().getGeoWrapper(params)
        }
    }

    public startLoginGeoProcess(bMandatory:boolean, reason: string = GEO_LOCATION_REASONS.REASON_LOGIN):Promise<void> {
        let geo = this.serviceFactory.userService().geoLocationFromLogin();
        return this.startGeoProcess(geo, bMandatory, reason);
    }

    public updateGeoPeriodic(geo:IGeoRequestParams):Promise<void> {
        geo.isGeoLocationInProcess = true;
        return this.startGeoProcess(geo, true, GEO_LOCATION_REASONS.REASON_PERIODIC);
    }

    public async startAutoRetryGeoProcess():Promise<void> {
        return this.startRetryGeoProcess(GEO_LOCATION_REASONS.AUTO_RETRY);
    }

    public async startRetryGeoProcess(reason?: string):Promise<void> {
        if (this.geoInProcess) {
            return this._runGeoInProcessFlow();
        }

        const geoRequestParams: IGeoRequestParams = await this.serviceFactory.geoRetryService().initGeoRetry(reason === GEO_LOCATION_REASONS.AUTO_RETRY? true : false);
        if(!geoRequestParams) {
            this._sendAnalytics(GEO_LOCATION.GA_RETRY_NOT_ELIGIBLE, null);
            this._showGeneralTechnicalError();
            return Promise.reject();
        }

        return this.startGeoProcess(geoRequestParams, true, reason ? reason : GEO_LOCATION_REASONS.RETRY);
    }

    public onGeoLocationResOk(geoLocationEncryptedResponse:string):void {
        this.geoComplyRetrynNumber = 0;
        this._sendAnalytics(GEO_LOCATION.GA_GEO_GEO_COMPLY_RESULT_OK, this.geoDataFormatter);
        this._validateGeoLocation(true, geoLocationEncryptedResponse);
    }

    public onGeoLocationResFailed(errorCode:number, errorMessage:string =''):void {
        this._sendAnalytics(GEO_LOCATION.GA_GEO_GEO_COMPLY_RESULT_FAILED, this.geoDataFormatter, {errorMessage: errorMessage});
        if(this._shouldRunGeoComplyRetry(errorCode)) {
            this.geoComplyRetrynNumber++;
            this._sendAnalytics(GEO_LOCATION.GEO_COMPLY_RETRY, this.geoDataFormatter);
            this.wrapper.getGeoLocation(this.geoDataFormatter);
        } else {
            this._sendAnalytics(GEO_LOCATION.GEO_COMPLY_FAIL_NO_RETRY, this.geoDataFormatter, {retryNumber: this.geoComplyRetrynNumber, errorCode: errorCode});

            this._validateGeoLocation(false, '', errorCode, errorMessage);
        }
    }

    public getServiceFactory():ServiceFactory{
        return this.serviceFactory;
    }

    public getButtonsForNonRetryGeoFlow(analyticsLabel: string) {
        if(!this._isEnable()) {
            return null;
        }

        const defaultButtons = [{
            name: this.serviceFactory.languageService().translation("logout_message_title").toUpperCase(),
            isClosable: true,
            callback: () => {
                this.serviceFactory.analyticsService().sendEvent({
                    category: GEO_LOCATION.GA_GEO_LOCATION_CATEGORY_SERVICE,
                    action: 'Location_Check_Click',
                    label: analyticsLabel,
                    value: 'Logout'
                });

                this.wrapper?.cancelGCConnect();
                this.serviceFactory.oAuth2Service().logout();
            }
        },
            {
                name: this.serviceFactory.languageService().translation("contact_us").toUpperCase(),
                isClosable: true,
                type: "secondary",
                callback: () => {

                    this.serviceFactory.analyticsService().sendOrbitUserFlowEvent({
                        category: 'Client_Message',
                        action: 'Location_Check_Click',
                        label: 'contact us'
                    });

                    this.wrapper?.cancelGCConnect();

                    this.serviceFactory.casinofiedAppLauncherProxyService().openContactUs({
                        businessCorrelationID: _.getGuid(),
                        channel: {
                            source: CHANNEL_SOURCE.geoLocation,
                            area: AreaType.button
                        }
                    });
                }
            }];

        const casinoInPokerOrSportButtons= [{
            name: this.serviceFactory.languageService().translation(GEO_LOCATION.GENERIC_OK_BUTTON),
            isClosable: true,
            callback: () => {
                this._closeGame();
            }
        }];

        if(this._isHybridCipOrCis()) {
            return casinoInPokerOrSportButtons;
        }

        return defaultButtons;
    }

    /**
     * should run geo comply retry only for 6XX erros and not 608 that related to license update so we need to get new license- retry to geo comply will not help.
     * @param errorCode
     * @private
     */
    private _shouldRunGeoComplyRetry(errorCode: number){
        return (
            this.geoComplyRetrynNumber < maxRetriesForGeoComply &&
            errorCode >= 600 && errorCode < 700 &&
            errorCode !== 608
        );
    }

    private _closeFreezeWindow():void {
        if (this.geoLocationFreezeWindow) {
            let closeDelay = this.initParams.antiFlickerTimer - (new Date().getTime() - this.geoLocationStartTime);
            if (closeDelay > 0) {
                DOM.setTimeout(() => this._doCloseFreezeWindow(), closeDelay);// prevent closing freeze window on too short interval (<5 sec)
            } else {
                this._doCloseFreezeWindow();
            }
        }
    }

    private startGeoProcess(geo:IGeoRequestParams, isMandatory:boolean, reason:string):Promise<void> {
        if (!this._isEnable()) {
            this._sendAnalytics(GEO_LOCATION.GA_GEO_DISABLED, null);
            return Promise.resolve();//feature disabled - continue to load game if needed
        }

        if (!this.wrapper) {
            this._sendAnalytics(GEO_LOCATION.GA_NOT_SUPPORTED_PLATFORM, null);
            return Promise.resolve();//feature not supported on webapp
        }

        if (!this.serviceFactory.oAuth2Service().isAuthorized()) {
            this.logger.debug('Geo - user not logged-in - ignore not relevant status changes');
            return Promise.resolve();
        }
        if (this.geoInProcess) {
            return this._runGeoInProcessFlow();
        }
        // isGeoLocationInProcess is property from platform that mean we need to do geo.
        if (!geo || !geo.isGeoLocationInProcess) {
            this._sendAnalytics(GEO_LOCATION.GA_NO_GEO_NEEDED_ACCORDING_TO_PLATFORM_PROPERTY, null);
            return Promise.resolve();
        }

        let cid:string = this.serviceFactory.userService().getCID()?.toString() || '';
        let geoDataFormatter: GeoDataFormatter = new GeoDataFormatter(geo, cid, isMandatory, reason);
        this.geoDataFormatter = geoDataFormatter;
        if (!geoDataFormatter.isDataValid()) {
            this._sendAnalytics(GEO_LOCATION.GA_GEO_NOT_VALID_REQUEST_FROM_PLATFORM, geoDataFormatter);
            Event.fire(SYSTEM_EVENTS.GEO_LOCATION_FAILED, 'geo data not valid');

            return Promise.reject();
        }

        this._setGeoInProcess(true);

        this._sendAnalytics(GEO_LOCATION.GA_GEO_START_GEO_PROCESS, geoDataFormatter, {reasonToStartGeo: reason});
        return this.getGeoLocation(geoDataFormatter);
    }

    private getGeoLocation(geoDataFormatter:GeoDataFormatter):Promise<void> {
        return this._isPlugInEnable(geoDataFormatter.isMandatory).catch(()=> {
            this._sendAnalytics(GEO_LOCATION.GA_GEO_PLUGIN_NOT_ENABLE, geoDataFormatter);
            Event.fire(SYSTEM_EVENTS.GEO_LOCATION_FAILED, 'plugin not enable');
            return Promise.reject();
        }).then(() =>{
            this._sendAnalytics(GEO_LOCATION.GA_GEO_PLUGIN_ENABLE, geoDataFormatter);
            this._getGeoLocationWhilePlayerMessagesReady(0, geoDataFormatter);
            if (!geoDataFormatter.isPeriodic && (!geoDataFormatter.isAutoRetryFlow || this.serviceFactory.gameRunnerProxyService().isOpenGameInProgress)) {
                this._closeGame();// if game is already opened, it should be explicitly closed (in case of play "real money" button from "demo mode")
                this._showFreezeWindow();
            }

            return new Promise<void>((resolve, reject) => {
                const geoFailEvent = this.onSystemEventOnce(SYSTEM_EVENTS.GEO_LOCATION_FAILED, ()=> {
                    geoSucceedEvent.off();
                    reject();
                });

                const geoSucceedEvent = this.onSystemEventOnce(SYSTEM_EVENTS.GEO_LOCATION_SUCCEED, ()=> {
                    geoFailEvent.off();
                    resolve();
                });
            });
        });
    }

    /* Subscriptions methods */

    private _subscribe():void {
        this.onSystemEvent(SYSTEM_EVENTS.OAUTH_STATE_CHANGE, this._onOauthChange);
        this.onSystemEvent(SYSTEM_EVENTS.GEO_LOCATION_FAILED, (message) => this._onGeoFailed(message));
        this.onSystemEvent(SYSTEM_EVENTS.GEO_LOCATION_SUCCEED, () => this._onGeoSuccess());
        this.onSystemEvent(SYSTEM_EVENTS.ON_INIT_COMPLETE, this._onInitComplete);
        this.onSystemEvent(SYSTEM_EVENTS.PLAYER_MESSAGES_STRATEGY_READY, this._onPlayerMessagesConnected);
        this.onSystemEvent(SYSTEM_EVENTS.GEO_KICK_EVENT, (kickParams) => this._onGeoKickEvent(kickParams));
    }

    private _unsubsribeRestictionChange() {
        if (this.restrictionChangedEvent) {
            this.restrictionChangedEvent.off();
            this.restrictionChangedEvent = null;
        }
    }

    private _onGeoFinish() {
        this._closeFreezeWindow();
        this._unsubsribeRestictionChange();
        this._setGeoInProcess(false);
    }

    private _onGeoSuccess() {
        this._sendAnalytics(GEO_LOCATION.GA_GEO_LOCATION_PROCESS_SUCCEED, this.geoDataFormatter);

        this._onGeoFinish();
        this.UpgradeSuccessGeoFlowCounter();
    }

    private _onGeoFailed(message: string) {
        this._sendAnalytics(GEO_LOCATION.GA_GEO_LOCATION_PROCESS_FAILED, this.geoDataFormatter, {
            successPreviousGeoFlowCounter: this.successGeoFlowCounter,
            failReason: message,
            isGeoInProcess: this.geoInProcess
        });

        this._onGeoFinish();
        this.resetSuccessGeoFlowCounter();
    }

    private _onGeoKickEvent(kickParams) {
        if (!_.isObject(kickParams) || kickParams.KickType !== KICK_TYPE.SOFT_KICK) {
            return;
        }

        if (getIfKnownKickReason(kickParams.KickReasonId)) {
            //In CIP8 Mini Orbit we just want to close the game, Poker will show the message
            if(Device.os.isMobileOS && this.serviceFactory.integrationService().isMiniOrbit()) {
                this._closeGame();
                Event.fire(SYSTEM_EVENTS.GEO_LOCATION_FAILED, 'cip8 mini orbit- kick received');
                return;
            }

            if(this._shouldRunGeoAutoRetryFlowCondition(kickParams)) {
                Event.fire(SYSTEM_EVENTS.GEO_LOCATION_FAILED, 'kick message received');
                this.startAutoRetryGeoProcess();
            } else {
                const isRetryFlow = this.serviceFactory.geoRetryService().isEligible(kickParams.TroubleshooterRetry) && !this._isHybridCipOrCis();
                this._showGeoKickMessage(kickParams, isRetryFlow);
            }
        }else {
            this.logger.debug('onGeoKickEvent - Kick was ignore since it is not a un known Kick Reason ID ');
            this._sendAnalytics(GEO_LOCATION.GA_GEO_LOCATION_KICK_EVENT_UN_KNOWN_REASON_ID, this.geoDataFormatter, kickParams);
        }
    }

    private _shouldRunGeoAutoRetryFlowCondition(kickParams) {
        return !this.geoDataFormatter?.isLoginFlow &&
               !this.serviceFactory.gameService().isGameRunning() &&
               this.serviceFactory.geoRetryService().isEligibleForAutoRetry(kickParams.TroubleshooterRetry, kickParams.KickReasonId, kickParams.DetailedMessages);
    }

    private _isHybridCipOrCis() {
        return Device.hybrid.inPoker || Device.hybrid.inSport;
    }

    private _showGeoKickMessage(kickParams, isRetry: boolean) {
        //In Casino we will show the message to the user that he got click and close the game
        let message = {
            text: this.serviceFactory.languageService().translation('kick_reason_' + kickParams.KickReasonId),
            customCSSClassList: ['cgp-mdp-text-support-slash-n'],
            buttons: [
                {
                    name: this.serviceFactory.languageService().translation('mdp_ok'),
                    callback: () => {
                        this.serviceFactory.analyticsService().sendOrbitUserFlowEvent({
                            category: 'Client_Message',
                            action: `kick_reason_${kickParams.KickReasonId}_popup`,
                            label: 'Close'
                        });
                        this._closeGame();
                    }
                }
            ]
        };
        if (getIfKnownGeoLockationKickReason(kickParams.KickReasonId)) {
            this._sendAnalytics(GEO_LOCATION.GA_GEO_LOCATION_KICK_EVENT, this.geoDataFormatter, kickParams);

            //if detailed messages were received, then overwrite default kick text with the ones received
            if (kickParams.DetailedMessages?.length > 0) {
                message.text = convertStringArrayToString(kickParams.DetailedMessages, '\n\n');
            };

            Object.assign(message, {
                title: this.serviceFactory.languageService().translation(GEO_LOCATION.GEO_IN_PROCESS_MSG_TITLE),
                closeButton: true,
                buttons: isRetry ? this.serviceFactory.geoRetryService().getRetryMessageButtons('kickGeoMessage') : this.getButtonsForNonRetryGeoFlow('kickGeoMessage'),
                btnCloseCallback: () => {
                    this.serviceFactory.analyticsService().sendOrbitUserFlowEvent({
                        category: 'Client_Message',
                        action: 'Location_Check_Click',
                        label: 'Close'
                    });
                    this._closeGame();
                }
            });
        }

        //We fire the message only if it is known Kick Reason
        if (message) {
            Event.fire(SYSTEM_EVENTS.MESSAGE_OPEN, message);
            Event.fire(SYSTEM_EVENTS.GEO_LOCATION_FAILED, 'kick message received');
        }
    }

    private _onPlayerMessagesConnected() {
        this.playerMessagesConnected = true;
    }

    private _onRestrictionChange() {
        this._sendAnalytics(GEO_LOCATION.GA_RESTRICTION_CHANGE, null);
        Event.fire(SYSTEM_EVENTS.GEO_LOCATION_SUCCEED);
    }

    private _onOauthChange():void {
        this.resetSuccessGeoFlowCounter();

        if (this.serviceFactory.oAuth2Service().isAuthorized()) {
            if(this.serviceFactory.gameRunnerProxyService().isOpenGameInProgress){
                this.startLoginGeoProcess(true);
            } else {
                this.startLoginGeoProcess(false);
            }
        } else {
            this._setGeoInProcess(false);
            this.playerMessagesConnected = false;
        }
    }

    private _onInitComplete():void {
        if (null !== this.wrapper) {
            this.wrapper.setParent(this);
            this.wrapper.runWrapper();
        }
        Event.fire(SYSTEM_EVENTS.GEO_HYBRID_UPDATE_ENABLE, this._isEnable());
    }

    /* end of subscriptions methods */


    private _isEnable():boolean {
        return this.initParams && this.initParams.enable;
    }

    private _isPlugInEnable(bMandatory:boolean):Promise<boolean> {
        return this.wrapper.isPlugInEnable(bMandatory);
    }

    private _setGeoInProcess(value: boolean) {
        this.geoInProcess = value;
        value ? this._setGeoLocationTimeout() : this._stopGeoLocationTimeout();
    }

    /**
     * we wait for firebase so we will not miss the update restriction message.
     * @param retryNumber
     * @param geoDataFormatter
     */
    private _getGeoLocationWhilePlayerMessagesReady(retryNumber: number, geoDataFormatter:GeoDataFormatter) {
        if(this.playerMessagesConnected === false && retryNumber < MAX_RETRIES_FOR_WAITING_PLAYER_MESSAGES_CONNECT) {
            setTimeout(()=> {
                this._getGeoLocationWhilePlayerMessagesReady(retryNumber + 1, geoDataFormatter);
                this.logger.debug('Geo location waiting for firebase connect. retry number:' + retryNumber);
            }, 400);
        }  else {
            if(retryNumber >= MAX_RETRIES_FOR_WAITING_PLAYER_MESSAGES_CONNECT) {
                this.logger.error(`Firebase fail to connect after ${retryNumber} retries`);
                this._sendAnalytics(GEO_LOCATION.GA_PLAYER_MESSAGES_NOT_CONNECT, geoDataFormatter,{playerMessagesConnectRetries: retryNumber});
            }

            // Will try also if firebase fail to connect.
            this.wrapper.getGeoLocation(geoDataFormatter).then(() => {
                this._clearGeoLocationFromLoginToken();
            });

            this._sendAnalytics(GEO_LOCATION.GA_GEO_LOCATION_REQUEST_SENT, geoDataFormatter,{playerMessagesConnectRetries: retryNumber});
        }
    }

    private _showFreezeWindow():void {
        this.geoLocationFreezeWindow = this.serviceFactory.orbitComponentRenderService().showGeoFreezeWindow();
        this.geoLocationStartTime = new Date().getTime();
        this._sendAnalytics(GEO_LOCATION.GA_GEO_SHOW_GEO_IN_PROGRESS_POPUP, this.geoDataFormatter);
    }

    private _stopGeoLocationTimeout():void{
        if (this.geoLocationTimeout) {
            DOM.clearTimeout(this.geoLocationTimeout);
        }
    }

    private _showGeneralTechnicalError() {
        let message = {
            text: this.serviceFactory.languageService().translation(GEO_LOCATION.TECHNICAL_PROBLEM_REASON),
            title: this.serviceFactory.languageService().translation(GEO_LOCATION.GEO_IN_PROCESS_MSG_TITLE),
            closeButton: true,
            buttons: this.getButtonsForNonRetryGeoFlow('GeoGeneralError'),
            btnCloseCallback: () => {
                this.serviceFactory.analyticsService().sendOrbitUserFlowEvent({
                    category: 'Client_Message',
                    action: 'Location_Check_Click',
                    label: 'Close'
                });
                this._closeGame();
            }};
        Event.fire(SYSTEM_EVENTS.MESSAGE_OPEN, message);
    }

    private _closeGame() {
        this.serviceFactory.gameService().closeRunningGame();
    }

    /**
     * we clean the geolocation property from login token to know that we already run the geo flow for this platform request.
     */
    private _clearGeoLocationFromLoginToken(){
        this.serviceFactory.userService().clearGeoLocationFromLogin();
        this._sendAnalytics(GEO_LOCATION.GA_GEO_CLEAN_GEO_PROPERTY_FROM_LOGIN_TOKEN, this.geoDataFormatter);
    }

    private _setGeoLocationTimeout() {
        this._stopGeoLocationTimeout();
        this._sendAnalytics(GEO_LOCATION.GEO_SET_LOCATION_TIMEOUT, null);
        this.geoLocationTimeout = DOM.setTimeout(() => {
            Event.fire(SYSTEM_EVENTS.GEO_LOCATION_FAILED, 'geo timeout reached');
            this._sendAnalytics(GEO_LOCATION.GEO_SET_LOCATION_TIMEOUT_REACHED, null);
            if(!this.geoDataFormatter.isPeriodic) {
                this._showGeneralTechnicalError();
            }
        }, this.initParams.geoProcessTimeout);// starting geo location Timeout (not longer then ~60 sec)
    }

    private _validateGeoLocation(resOk:boolean, msg:string, errorCode = -1, errorMsg = ''):void {
        const additionalDataForAnalytics = {result:resOk, errorCode:errorCode};

        this._sendAnalytics(GEO_LOCATION.GA_GEO_LOCATION_VALIDATION, this.geoDataFormatter, additionalDataForAnalytics);
        if (!this.restrictionChangedEvent) {
            this.restrictionChangedEvent = this.onSystemEvent(SYSTEM_EVENTS.RESTRICTIONS_CHANGED, () => this._onRestrictionChange());// normal closing geo message on getting restriction response
        }
        this.serviceFactory.userService().validateGeoLocation(msg, this.geoDataFormatter.isPeriodic, errorCode, errorMsg).then((data) => {
            this._sendAnalytics(GEO_LOCATION.GA_GEO_LOCATION_SUCCEEDED_VALIDATE_GEO_LOCATION, this.geoDataFormatter, additionalDataForAnalytics);
        }).catch(() => {
            this._sendAnalytics(GEO_LOCATION.GA_GEO_LOCATION_FAIL_VALIDATE_GEO_LOCATION, this.geoDataFormatter, additionalDataForAnalytics);
        });
    }

    private _sendAnalytics(action:string, geoData:GeoDataFormatter, additionalData:object|null=null):void {
        let wrapper:string = this.wrapper ? this.wrapper.constructor.name : NONE;
        let data = {
            wrapper: wrapper
        };
        if(geoData) {
            data = Object.assign(data, {
                isMandatory: geoData.isMandatory,
                isPeriodicFlow: geoData.isPeriodic,
                isDataValid: geoData.isDataValid(),
                isRetryFlow: geoData.isRetryFlow,
                isAutoRetryFlow: geoData.isAutoRetryFlow,
            });
        }

        data = Object.assign(data, additionalData);
        let analyticsData:any = {
            CGPEventCategory: GEO_LOCATION.GA_GEO_LOCATION_CATEGORY_SERVICE,
            CGPEventAction: action
        };
        analyticsData.geoData = data;

        this.logger.debug(action, JSON.stringify(analyticsData));
        this.serviceFactory.structuredLoggingService().getLogger(analyticsData).info();
    }

    private _doCloseFreezeWindow():void {
        let geoProcessTime:string = String(new Date().getTime() - this.geoLocationStartTime);
        this._sendAnalytics(GEO_LOCATION.GA_GEO_LOCATION_POPUP_CLOSED,this.geoDataFormatter,{geoProcessTime:geoProcessTime});
        this.geoLocationFreezeWindow && this.geoLocationFreezeWindow.close();
        this.geoLocationFreezeWindow = null;
    }

    private _isParamsValid(params):boolean {
        return !(_.isNullOrUndefined(params) || _.isNullOrUndefined(params.Hybrid) || _.isNullOrUndefined(params.PC));
    }

    private resetSuccessGeoFlowCounter() {
        Store.set(successGeoFlowCounterKey, 0);
    }

    //we need this property to know how many geo success flows we have until user fail.
    private get successGeoFlowCounter() {
        return _.safeJSON(Store.get(successGeoFlowCounterKey), 0);
    }

    private UpgradeSuccessGeoFlowCounter() {
        Store.set(successGeoFlowCounterKey, this.successGeoFlowCounter + 1);
    }

    private _runGeoInProcessFlow(): Promise<void> {
        this._sendAnalytics(GEO_LOCATION.GA_GEO_ALREADY_IN_PROCESS, null);
        return new Promise((resolve, reject)=> {
            const geoFailEvent = this.onSystemEventOnce(SYSTEM_EVENTS.GEO_LOCATION_FAILED, ()=> {
                geoSucceedEvent.off();
                reject();
            });

            const geoSucceedEvent = this.onSystemEventOnce(SYSTEM_EVENTS.GEO_LOCATION_SUCCEED, ()=> {
                geoFailEvent.off();
                resolve();
            });
        });
    }
}
